// Roster generator settings, grouped by topic (Admin → Roster generator and the
// Generate dialog's "Variant settings"). Titles, sentences and trade-offs of the
// rules come from the server (GET /shifts/generator-settings → rules); this file
// only decides the order, which number settings belong to which rule, and the
// wording of those number fields.

// Topic order; an array = rules shown together (they pull in opposite directions)
export const TOPICS = [
  'target',
  'preferences',
  ['weekly_spread', 'blocks'],
  'variety',
  'pairing',
  'event_fairness',
];

export const OPPOSITE_NOTE =
  'These two pull in opposite directions: one keeps weeks even, the other bunches shifts together. Raising one weakens the other.';

// Number settings per rule (app_settings keys; ranges as validated on the server)
export const NUMBER_FIELDS = {
  blocks: [
    {
      key: 'CLUSTER_THRESHOLD', label: 'From … shifts per week', short: 'from {v} shifts/week',
      min: 1, max: 7, step: 0.5,
      hint: 'Only people whose preferred shifts per week reach this number get blocks. Without preferred shifts, contract hours are used.',
    },
    {
      key: 'MAX_STREAK', label: 'Ideal block length', short: 'ideal block {v} days',
      min: 2, max: 7, step: 1,
      hint: "Soft goal for grouping. Can't be higher than the absolute max days in a row.",
    },
  ],
  variety: [
    {
      key: 'VARIETY_MAX_IN_ROW', label: 'Max same type in a row', short: 'max {v} in a row',
      min: 1, max: 6, step: 1,
      hint: 'Regular shifts only, also across plans.',
    },
    {
      key: 'VARIETY_MAX_PER_WEEK', label: 'Max same type per week (Mon–Sun)', short: 'max {v} per week',
      min: 1, max: 7, step: 1,
      hint: 'Counts the week, also when the days are not in a row.',
    },
  ],
};

// Hard limit for everyone (no level) — shown at the top of the generator card
export const HARD_FIELDS = [
  {
    key: 'MAX_DAYS_IN_ROW', label: 'Absolute max days in a row', short: 'max {v} days in a row (hard limit)',
    min: 3, max: 7, step: 1,
    hint: 'Hard limit for everyone. Only admins can make exceptions by hand.',
  },
];

export const ALL_NUMBER_FIELDS = [...HARD_FIELDS, ...Object.values(NUMBER_FIELDS).flat()];

// Ideal block length (soft) can't be above the absolute max (hard)
export function blockLengthError(numbers) {
  const ideal = Number(numbers?.MAX_STREAK);
  const max = Number(numbers?.MAX_DAYS_IN_ROW);
  if (!Number.isFinite(ideal) || !Number.isFinite(max) || numbers?.MAX_DAYS_IN_ROW === '' || numbers?.MAX_STREAK === '') return null;
  return ideal > max ? `Ideal block length (${ideal}) can't be higher than the absolute max days in a row (${max}).` : null;
}

export const flattenTopics = (topics = TOPICS) => topics.flat();

// Rule objects (from the server) in topic order: [{ rule } | { group: [rule, rule] }]
export function topicRules(rules = []) {
  const byKey = Object.fromEntries(rules.map((r) => [r.key, r]));
  return TOPICS.map((t) => (Array.isArray(t)
    ? { group: t.map((k) => byKey[k]).filter(Boolean) }
    : { rule: byKey[t] }))
    .filter((t) => t.rule || t.group?.length);
}

export const levelLabel = (meta, key) => meta?.level_options?.find((o) => o.key === key)?.label || key;

const missing = (x) => x == null || x === '';
// Both missing (e.g. a value older data doesn't have) counts as the same
const sameNumber = (a, b) => (missing(a) && missing(b)) || (!missing(a) && !missing(b) && Number(a) === Number(b));

// What differs between two states, for confirmations and "Changed" markers:
// [{ key, label, from, to }] with display texts, levels first (topic order)
export function changesBetween(meta, from, to) {
  const out = [];
  const ruleByKey = Object.fromEntries((meta?.rules || []).map((r) => [r.key, r]));
  for (const key of flattenTopics()) {
    const rule = ruleByKey[key];
    if (!rule || !from.levels || !to.levels) continue;
    if (from.levels[key] !== to.levels[key]) {
      out.push({ key, label: rule.label, from: levelLabel(meta, from.levels[key]), to: levelLabel(meta, to.levels[key]) });
    }
  }
  if (from.numbers && to.numbers) {
    for (const f of ALL_NUMBER_FIELDS) {
      if (!sameNumber(from.numbers[f.key], to.numbers[f.key])) {
        out.push({ key: f.key, label: f.label, from: String(from.numbers[f.key] ?? '—'), to: String(to.numbers[f.key] ?? '—') });
      }
    }
  }
  return out;
}

export const numberDiffers = (a, b) => !sameNumber(a, b);

// Client-side range check (the server validates again): error text or null
export function numberError(field, value) {
  const n = Number(value);
  if (value === '' || value == null || !Number.isFinite(n)) return `${field.label}: enter a number.`;
  if (n < field.min || n > field.max) return `${field.label}: between ${field.min} and ${field.max}.`;
  if (Math.abs(n / field.step - Math.round(n / field.step)) > 1e-9) {
    return field.step === 1 ? `${field.label}: a whole number.` : `${field.label}: in steps of ${field.step}.`;
  }
  return null;
}
